/* Inline 1.6px-stroke icons. Inlined rather than pulled from a pack so the bundle carries
   only the dozen glyphs actually used, and so they inherit currentColor everywhere. */

type P = { size?: number };
const s = (n = 18) => ({
  width: n, height: n, viewBox: '0 0 24 24', fill: 'none',
  stroke: 'currentColor', strokeWidth: 1.6, strokeLinecap: 'round' as const,
  strokeLinejoin: 'round' as const, 'aria-hidden': true,
});

export const IconMenu = ({ size }: P) => (
  <svg {...s(size)}><path d="M3 6h18M3 12h18M3 18h18" /></svg>
);
export const IconClose = ({ size }: P) => (
  <svg {...s(size)}><path d="M18 6 6 18M6 6l12 12" /></svg>
);
export const IconSparkles = ({ size }: P) => (
  <svg {...s(size)}>
    <path d="M12 3v4M12 17v4M3 12h4M17 12h4M5.6 5.6l2.8 2.8M15.6 15.6l2.8 2.8M18.4 5.6l-2.8 2.8M8.4 15.6l-2.8 2.8" />
  </svg>
);
export const IconChat = ({ size }: P) => (
  <svg {...s(size)}><path d="M21 11.5a8.4 8.4 0 0 1-9 8.4L3 21l1.1-4.6A8.4 8.4 0 1 1 21 11.5Z" /></svg>
);
export const IconGauge = ({ size }: P) => (
  <svg {...s(size)}><path d="M12 14a2 2 0 1 0 0-4 2 2 0 0 0 0 4Z" /><path d="M13.4 10.6 17 7M3.5 18a9.5 9.5 0 1 1 17 0" /></svg>
);
export const IconActivity = ({ size }: P) => (
  <svg {...s(size)}><path d="M3 12h4l3 8 4-16 3 8h4" /></svg>
);
/* The rail toggle. A panel glyph beats an activity waveform here: the button opens a panel,
   and the waveform's right half disappeared under the unread badge. */
export const IconPanelRight = ({ size }: P) => (
  <svg {...s(size)}>
    <rect x="3" y="4" width="18" height="16" rx="2.5" />
    <path d="M14 4v16" />
  </svg>
);
export const IconArrowUp = ({ size }: P) => (
  <svg {...s(size)} strokeWidth={2}><path d="M12 19V5M5 12l7-7 7 7" /></svg>
);
export const IconStop = ({ size }: P) => (
  <svg {...s(size)}><rect x="7" y="7" width="10" height="10" rx="2.5" fill="currentColor" stroke="none" /></svg>
);
export const IconArrowRight = ({ size }: P) => (
  <svg {...s(size)}><path d="M5 12h14M12 5l7 7-7 7" /></svg>
);
export const IconCheck = ({ size }: P) => (
  <svg {...s(size)} strokeWidth={2}><path d="m4 12 5.5 5.5L20 7" /></svg>
);
export const IconShield = ({ size }: P) => (
  <svg {...s(size)}><path d="M12 3l7.5 3v5.5c0 4.6-3.1 8.3-7.5 9.5-4.4-1.2-7.5-4.9-7.5-9.5V6L12 3Z" /><path d="m9 12 2 2 4-4" /></svg>
);
export const IconBolt = ({ size }: P) => (
  <svg {...s(size)}><path d="M13 2 4.5 13H11l-1 9 8.5-11H12l1-9Z" /></svg>
);
export const IconSignOut = ({ size }: P) => (
  <svg {...s(size)}><path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4M16 17l5-5-5-5M21 12H9" /></svg>
);

/** Google's mark, in its official four colours — a monochrome version reads as unofficial. */
export const IconGoogle = ({ size = 18 }: P) => (
  <svg width={size} height={size} viewBox="0 0 48 48" aria-hidden>
    <path fill="#FFC107" d="M43.6 20.1H42V20H24v8h11.3C33.7 32.7 29.2 36 24 36c-6.6 0-12-5.4-12-12s5.4-12 12-12c3.1 0 5.8 1.2 7.9 3.1l5.7-5.7C34.1 6.1 29.3 4 24 4 13 4 4 13 4 24s9 20 20 20 20-9 20-20c0-1.3-.1-2.6-.4-3.9Z" />
    <path fill="#FF3D00" d="m6.3 14.7 6.6 4.8C14.7 15.1 19 12 24 12c3.1 0 5.8 1.2 7.9 3.1l5.7-5.7C34.1 6.1 29.3 4 24 4 16.3 4 9.7 8.3 6.3 14.7Z" />
    <path fill="#4CAF50" d="M24 44c5.2 0 9.9-2 13.4-5.2l-6.2-5.2C29.2 35.1 26.7 36 24 36c-5.2 0-9.6-3.3-11.3-7.9l-6.5 5C9.5 39.6 16.2 44 24 44Z" />
    <path fill="#1976D2" d="M43.6 20.1H42V20H24v8h11.3c-.8 2.2-2.2 4.2-4.1 5.6l6.2 5.2C39 36.2 44 31 44 24c0-1.3-.1-2.6-.4-3.9Z" />
  </svg>
);

/** The brand mark: a gradient L on the dark plate, matching the logo asset. */
export function Mark({ size = 26 }: P) {
  const id = `lxg${size}`;
  return (
    <svg width={size} height={size} viewBox="0 0 26 26" aria-hidden>
      <defs>
        <linearGradient id={id} x1="0" y1="0" x2="1" y2="1">
          <stop offset="0%" stopColor="#FFCE72" />
          <stop offset="45%" stopColor="#EE3B3B" />
          <stop offset="100%" stopColor="#6E4FD0" />
        </linearGradient>
      </defs>
      <rect width="26" height="26" rx="7.5" fill="#0A0A0C" />
      <path d="M9 6h3.2v10.4H19V19H9V6Z" fill={`url(#${id})`} />
    </svg>
  );
}
